import {
  ComponentType,
  CSSProperties,
  useCallback,
  useMemo,
  useState,
} from "react"

import { ColorMap, Status } from "components/Status"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Switch, TablePagination } from "@material-ui/core"
import {
  activateClientAgencyConfigCopy,
  deactivateClientAgencyConfigCopy,
  deleteClientAgencyConfigCopy,
  fetchClientAgencyAggregations,
  fetchClientAgencyConfigCopies,
} from "api/agencies"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import { CopyResource } from "models/copies"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import MoreActions from "components/MoreActions"
import { StyledTable } from "components/Rable/StyledTable"
import { UpdateCopyCodeModal } from "./UpdateCopyCodeModal"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { CheckList, CheckListItemLabel } from "components/inputs/CheckList"
import { Filters } from "components/tables"
import { useLocationSearch } from "hooks/useLocationSearch"
import { TableToolbar } from "components/tables/TableToolbar"
import { DateRangeButtonTrigger } from "components/inputs/DateRange"
import { zonedTimeToUtc } from "date-fns-tz"
import { endOfDay, startOfDay } from "date-fns"
import { IAgencyClientCopiesAggregations, IAggregations } from "models/agency"
import { limitAndOffset } from "api/utils"

type Col = {
  name: string
  cell: ComponentType<CellProps>
  align?: "center" | "left" | "right"
  width?: CSSProperties["width"]
}
interface IUpdateCodeModalData {
  externalCode?: string
  id?: string
}

type CellProps = {
  row: CopyResource
  name: string
  onActivate: (row: CopyResource) => void
  onDeactivate: (id: CopyResource) => void
  onDelete: (row: CopyResource) => void
  setCodeUpdateModalData: (props: IUpdateCodeModalData) => void
}

const generateColumns = (actionAllowed: boolean) => {
  const c: Col[] = [
    { name: "name", cell: ReadOnlyCell, align: "left" },
    { name: "productName", cell: ReadOnlyCell, align: "left" },
    { name: "mediaSemanticCode", cell: ReadOnlyCell, align: "left" },
    { name: "externalCode", cell: ReadOnlyCell, align: "left" },
    { name: "duration", cell: ReadOnlyCell, align: "left" },
    { name: "language", cell: ReadOnlyCell, align: "left" },
    { name: "creativeAgencyName", cell: ReadOnlyCell, align: "left" },
    { name: "copyQualityName", cell: ReadOnlyCell, align: "left" },
    { name: "integrationStatus", cell: IntegrationStatus, align: "left" },
    { name: "error", cell: ReadOnlyCell, align: "left" },
    { name: "dateCreated", cell: DateCell, align: "left" },
  ]

  actionAllowed &&
    c.push(
      { name: "status", cell: StatusCell, align: "left" },
      {
        name: "actions",
        cell: ActionCell,
        align: "center",
        width: "80px",
      },
    )

  return c
}

export default function AgencyClientCopies() {
  const { t } = useI18n()
  const { configId } = useParams<{ configId: string }>()
  const { hasAuthority, meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}

  const [codeUpdateModalData, setCodeUpdateModalData] =
    useState<IUpdateCodeModalData>({})
  const [deleteModalInfo, setDeleteModalInfo] = useState<ModalInfo>(undefined)

  const actionAllowed = hasAuthority(["AUTH_MANAGE_COPY"])
  const feedback = useSendFeedBack()
  const locationSearch = useLocationSearch()

  const PAGE_SIZE = 10

  const page = locationSearch.getNumber("page", 0)
  const pageSize = locationSearch.getNumber("pageSize", PAGE_SIZE)

  const startDate = locationSearch.get("startDate")
  const endDate = locationSearch.get("endDate")

  const params = useMemo(
    () => locationSearch.map((p) => limitAndOffset(p, pageSize)).toString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locationSearch.search],
  )

  const { data, isLoading } = useQuery(
    ["client-agency-config-copies", configId, params],
    fetchClientAgencyConfigCopies,
  )

  const { data: aggregations, updatedAt: aggregationsUpdatedAt } = useQuery(
    ["client-agency-config-ags", configId],
    fetchClientAgencyAggregations,
  )

  const formatName = (
    option: string | number | IAggregations,
    optionType?: keyof IAgencyClientCopiesAggregations,
  ) => {
    const name =
      typeof option === "string" || typeof option === "number"
        ? String(option)
        : option.name

    if (optionType === "languages") return name

    return name.toUpperCase()
  }

  /**
   * Formats options
   * @param options Options to format
   * @param isStatus Is the ooption status
   * @param sort Sort direction for numbers
   * @param optionType The type of option
   * @returns Formated options
   */
  const formatOptions = (
    options: IAggregations[] | string[] | number[],
    isStatus?: boolean,
    sort?: "desc",
    optionType?: keyof IAgencyClientCopiesAggregations,
  ): {
    value: string
    label: JSX.Element
    searchLabel: string
  }[] =>
    [...options]
      .sort(
        (
          a: string | number | IAggregations,
          b: string | number | IAggregations,
        ) => {
          if (
            sort === "desc" &&
            typeof a === "number" &&
            typeof b === "number"
          ) {
            return Number(a) - Number(b)
          } else {
            return (
              typeof a === "string" || typeof a === "number"
                ? String(a)
                : a.name
            )
              .trim()
              .localeCompare(
                (typeof b === "string" || typeof b === "number"
                  ? String(b)
                  : b.name
                ).trim(),
              )
          }
        },
      )
      .map((option: string | number | IAggregations) => {
        const value =
          typeof option === "string" || typeof option === "number"
            ? String(option)
            : option.id

        const name = formatName(option, optionType)

        return {
          value,
          label: (
            <CheckListItemLabel
              secondary={""}
              primary={
                !isStatus ? (
                  name
                ) : (
                  <Status className={styles.capitalize} color={ColorMap[name]}>
                    {name}
                  </Status>
                )
              }
            />
          ),
          searchLabel: name,
        }
      }) || []

  const filters = useMemo(() => {
    const mediaTypeOptions = formatOptions(aggregations?.mediaTypes ?? [])

    const products = formatOptions(aggregations?.products ?? [])

    const creativeAgencies = formatOptions(aggregations?.creativeAgencies ?? [])

    const copyQualities = formatOptions(aggregations?.copyQualities ?? [])

    const integrationStatuses = formatOptions(
      aggregations?.integrationStatuses ?? [],
      true,
    )

    const languages = formatOptions(
      aggregations?.languages ?? [],
      undefined,
      undefined,
      "languages",
    )

    const duration = formatOptions(
      aggregations?.durations ?? [],
      undefined,
      "desc",
    )

    const options = [
      {
        name: "productIds",
        label: "filters.products",
        options: products,
      },
      {
        name: "mediaTypeSemantics",
        label: "filters.mediaType",
        options: mediaTypeOptions,
      },
      {
        name: "creativeAgenciesIds",
        label: "filters.creativeAgencies",
        options: creativeAgencies,
      },
      {
        name: "copyQualitiesIds",
        label: "filters.copyQualities",
        options: copyQualities,
      },
      {
        name: "integrationStatuses",
        label: "filters.integrationStatuses",
        options: integrationStatuses,
      },
      {
        name: "languages",
        label: "filters.languages",
        options: languages,
      },
      {
        name: "durations",
        label: "agency.client.copies.duration",
        options: duration,
        disableMaxOptions: true,
        className: styles.duration,
      },
    ]

    return options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregationsUpdatedAt])

  const activeFiltersCount = locationSearch.countAll(
    filters.map((flt) => flt.name),
  )

  const onDateFilterReset = useCallback(() => {
    locationSearch.set("startDate", "")
    locationSearch.set("endDate", "")
    locationSearch.set("page", "")
  }, [locationSearch])

  const onDateFilterChange = (s: Date | null, e: Date | null) => {
    s &&
      locationSearch.set(
        "startDate",
        zonedTimeToUtc(startOfDay(s), timeZone).toISOString(),
      )
    e &&
      locationSearch.set(
        "endDate",
        zonedTimeToUtc(endOfDay(e), timeZone).toISOString(),
      )
    locationSearch.set("page", "")
  }

  const handleFiltersReset = () => {
    filters
      .map((flt) => flt.name)
      .concat(["page"])
      .forEach((k) => {
        locationSearch.set(k, "")
      })
  }

  const [remove] = useMutation(deleteClientAgencyConfigCopy, {
    onSuccess: () => {
      setDeleteModalInfo(undefined)
      queryCache.invalidateQueries("client-agency-config-copies")
    },
    onError: (err) => {
      setDeleteModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const onDelete = useCallback(
    (row: CopyResource) => {
      setDeleteModalInfo({
        title: t("agency.client.copies.modal.delete.title"),
        type: "primary_destructive",
        description: t("agency.client.copies.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: async () =>
          await remove({
            clientAgencyConfigId: configId || "",
            copyId: row?.id || "",
          }),
      })
    },
    [remove, t, configId],
  )

  const [activate] = useMutation(activateClientAgencyConfigCopy, {
    onSuccess: () => {
      setDeleteModalInfo(undefined)
      queryCache.invalidateQueries("client-agency-config-copies")
    },
    onError: (err) => {
      setDeleteModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const onActivate = useCallback(
    (row: CopyResource) => {
      setDeleteModalInfo({
        title: t("agency.client.copies.modal.activate.title"),
        description: t("agency.client.copies.modal.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () =>
          await activate({
            clientAgencyConfigId: configId || "",
            copyId: row?.id || "",
          }),
      })
    },
    [activate, t, configId],
  )

  const [deactivate] = useMutation(deactivateClientAgencyConfigCopy, {
    onSuccess: async () => {
      setDeleteModalInfo(undefined)
      queryCache.invalidateQueries("client-agency-config-copies")
    },
    onError: (err) => {
      setDeleteModalInfo(undefined)
      feedback(t(err.message), "error")
    },
  })

  const onDeactivate = useCallback(
    (row: CopyResource) => {
      setDeleteModalInfo({
        type: "primary_destructive",
        title: t("agency.client.copies.modal.deactivate.title"),
        description: t("agency.client.copies.modal.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () =>
          await deactivate({
            clientAgencyConfigId: configId || "",
            copyId: row?.id || "",
          }),
      })
    },
    [deactivate, t, configId],
  )

  const isModalOpen = Boolean(deleteModalInfo)

  const cols = generateColumns(!!actionAllowed)

  const copies = data?.data

  return (
    <div className={styles.container}>
      <TableToolbar>
        <div className="left">
          {filters.length > 0 ? (
            <Filters
              count={activeFiltersCount}
              onReset={handleFiltersReset}
              title={t("agency.client.filter.title")}
            >
              {filters.map((fltr) => {
                const totalChecked = fltr.options
                  .map((option) =>
                    locationSearch.contains(fltr.name, option.value),
                  )
                  .filter(Boolean).length
                const options = fltr.options.map((option, index) => {
                  const checked = locationSearch.contains(
                    fltr.name,
                    option.value?.trim(),
                  )
                  return {
                    ...option,
                    checked,
                    setValue: (name: string, value: any) => {
                      locationSearch.append(name, value)
                      locationSearch.set("page", 0)
                    },
                  }
                })

                const label = t(fltr.label)
                return (
                  <CheckList
                    key={fltr.name}
                    name={fltr.name}
                    label={label}
                    options={options}
                    totalChecked={totalChecked}
                    fieldsetClassName={fltr.className}
                    disableMaxOptions={fltr.disableMaxOptions}
                  />
                )
              })}
            </Filters>
          ) : null}
          <DateRangeButtonTrigger
            onRangeChange={onDateFilterChange}
            onRangeClear={onDateFilterReset}
            start={startDate}
            end={endDate}
            clearLabel={t("cancel")}
            placeholder={t("date.range.filter.placeholder")}
            hasStartAdornment
            asTextButton
          />
        </div>
      </TableToolbar>

      {isLoading ? (
        <Loading />
      ) : copies?.length ? (
        <>
          <StyledTable className={styles.table}>
            <thead>
              <tr role="row">
                {cols.map((col) => (
                  <th
                    key={col.name}
                    role="columnheader"
                    style={{ width: col.width }}
                  >
                    <span>{t(`agency.client.copies.${col.name}`)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {copies?.map((row, rIndex) => (
                <tr key={rIndex} role="row">
                  {cols.map((col, cIndex) => {
                    const { name, cell: Cell, align = "left" } = col
                    return (
                      <td className={align} key={`td${cIndex}`}>
                        <Cell
                          row={row}
                          name={name}
                          onActivate={onActivate}
                          onDeactivate={onDeactivate}
                          onDelete={onDelete}
                          setCodeUpdateModalData={setCodeUpdateModalData}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </StyledTable>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={data?.total || 0}
            rowsPerPage={pageSize}
            page={page}
            onChangePage={(_: any, page: number) => {
              locationSearch.set("page", page)
            }}
            onChangeRowsPerPage={(evt) => {
              locationSearch.set("page", 0)
              locationSearch.set("pageSize", String(evt.target.value))
            }}
            classes={{ caption: "BodyBody-sDark-greyRegular" }}
          />
        </>
      ) : (
        <EmptyState title={t("agency.client.copies.empty.table")} />
      )}
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setDeleteModalInfo(undefined)}
        {...deleteModalInfo}
      />
      <UpdateCopyCodeModal
        open={!!codeUpdateModalData.id}
        defaultValue={codeUpdateModalData.externalCode}
        onClose={() => setCodeUpdateModalData({})}
        configId={configId}
        copyId={codeUpdateModalData.id}
      />
    </div>
  )
}

function ReadOnlyCell(props: CellProps) {
  const { row, name } = props
  return <div>{row[name as keyof CopyResource]}</div>
}

function IntegrationStatus(props: CellProps) {
  const { row } = props
  const status = row.integrationStatus.toLocaleLowerCase()
  return (
    <Status
      className={styles.capitalize}
      color={ColorMap[row.integrationStatus]}
    >
      {status}
    </Status>
  )
}

function StatusCell(props: CellProps) {
  const { row, onActivate, onDeactivate } = props

  return (
    <Switch
      name="agencyClientCopies"
      value={row.status}
      color="primary"
      defaultChecked={row.status === "ACTIVE"}
      checked={row.status === "ACTIVE"}
      onChange={
        row.status !== "ACTIVE"
          ? () => onActivate(row)
          : () => onDeactivate(row)
      }
    />
  )
}

function ActionCell(props: CellProps) {
  const { row, onDelete, setCodeUpdateModalData } = props
  const { t } = useI18n()
  return (
    <MoreActions
      size="small"
      options={[
        {
          label: t("copies.action.updateCode"),
          onClick: () =>
            setCodeUpdateModalData({
              externalCode: row.externalCode,
              id: row.id,
            }),
          hidden: row.integrationStatus === "SUCCESS",
        },
        {
          label: t("copies.action.delete"),
          onClick: () => onDelete(row),
          hidden: row.status !== "INACTIVE",
          labelStyle: styles.deleteActionLabel,
        },
      ]}
    />
  )
}

function DateCell(props: CellProps) {
  const { dateFormatter } = useAuthenticated()
  const { row } = props
  if (row.dateCreated === null) return null
  return (
    <div className={cx("BodyBody-2BlackRegular", styles.numeric)}>
      {dateFormatter(row.dateCreated)}
    </div>
  )
}

const styles = {
  container: css`
    padding: var(--spacing-xxl-2) 0px 0px;
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
      &[data-column-id="billingSubtypes"],
      &[data-column-id="semanticName"] {
        display: flex;
        flex: 1;
        justify-content: flex-start;
        padding-left: var(--spacing-s);
      }
      display: flex;
      flex: 1;
      justify-content: flex-start;
      padding-left: var(--spacing-xs);
    }

    .BodyBody-2BlackRegular {
      margin: 0;
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  numeric: css`
    text-align: center;
  `,
  capitalize: css`
    text-transform: capitalize;
  `,
  deleteActionLabel: css`
    * {
      color: var(--dark-red);
    }
  `,
  duration: css`
    display: grid;
    grid-template-columns: repeat(3, minmax(50px, auto));
    grid-gap: 4px;
  `,
}
